/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  GetMcpServerRequest,
  GetMcpServerSecurity,
} from "../models/operations/getmcpserver.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildGetMcpServerQuery,
  GetMcpServerQueryData,
  prefetchGetMcpServer,
  queryKeyGetMcpServer,
} from "./getMcpServer.core.js";
export {
  buildGetMcpServerQuery,
  type GetMcpServerQueryData,
  prefetchGetMcpServer,
  queryKeyGetMcpServer,
};

export type GetMcpServerQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * getMcpServer mcpServers
 *
 * @remarks
 * Get an MCP server by ID, slug, or backing toolset ID. Exactly one selector must be provided. Toolset lookup prefers enabled servers, then the canonical wrapper, then the oldest server.
 */
export function useGetMcpServer(
  request?: GetMcpServerRequest | undefined,
  security?: GetMcpServerSecurity | undefined,
  options?: QueryHookOptions<GetMcpServerQueryData, GetMcpServerQueryError>,
): UseQueryResult<GetMcpServerQueryData, GetMcpServerQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildGetMcpServerQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * getMcpServer mcpServers
 *
 * @remarks
 * Get an MCP server by ID, slug, or backing toolset ID. Exactly one selector must be provided. Toolset lookup prefers enabled servers, then the canonical wrapper, then the oldest server.
 */
export function useGetMcpServerSuspense(
  request?: GetMcpServerRequest | undefined,
  security?: GetMcpServerSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    GetMcpServerQueryData,
    GetMcpServerQueryError
  >,
): UseSuspenseQueryResult<GetMcpServerQueryData, GetMcpServerQueryError> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildGetMcpServerQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setGetMcpServerData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      id?: string | undefined;
      slug?: string | undefined;
      toolsetId?: string | undefined;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: GetMcpServerQueryData,
): GetMcpServerQueryData | undefined {
  const key = queryKeyGetMcpServer(...queryKeyBase);

  return client.setQueryData<GetMcpServerQueryData>(key, data);
}

export function invalidateGetMcpServer(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      id?: string | undefined;
      slug?: string | undefined;
      toolsetId?: string | undefined;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "mcpServers", "get", ...queryKeyBase],
  });
}

export function invalidateAllGetMcpServer(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "mcpServers", "get"],
  });
}
